const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');

const FACE_API_LOCAL_SCRIPT = '/static/vendor/face-api/face-api.min.js';
const FACE_API_CDN_SCRIPT = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js';

const FACE_API_LOCAL_MODEL_URL = '/static/models/face-api';
const FACE_API_CDN_MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights';

const sectionLabel = text => ({
    blockType: BlockType.LABEL,
    text
});

class MadBotFaceAIEngine {
    constructor () {
        this.runtime = null;

        this.initialized = false;
        this.initializing = false;
        this.initializePromise = null;

        this.cameraOn = false;
        this.cameraSource = 'front';
        this.detectionMode = 'fast';
        this.confidenceThreshold = 70;

        this.showPreview = true;
        this.cameraTransparency = 0;

        this.overlay = {
            faceBox: false,
            nameLabel: false,
            expressionLabel: false,
            facePoints: false
        };

        this.recognitionEnabled = false;

        this.stream = null;
        this.video = null;
        this.canvas = null;
        this.ctx = null;
        this.container = null;
        this.stageHost = null;

        this.faces = [];
        this.runningLoop = false;

        this.trainedFaces = {};
        this.faceMatcher = null;
        this.lastRecognizedName = '';
        this.lastRecognizedConfidence = 0;

        this.loadDatabase();

        setTimeout(() => {
            this.initialize().catch(error => {
                this.showLoadingError(error);
            });
        }, 300);
    }

    setRuntime (runtime) {
        this.runtime = runtime;
    }

    showLoadingPopup (message) {
        let popup = document.getElementById('madbot-face-ai-loader');

        if (!popup) {
            popup = document.createElement('div');
            popup.id = 'madbot-face-ai-loader';

            Object.assign(popup.style, {
                position: 'fixed',
                inset: '0',
                background: 'rgba(0, 0, 0, 0.55)',
                zIndex: 999999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Arial, sans-serif'
            });

            popup.innerHTML = `
                <div style="
                    width: 360px;
                    background: #ffffff;
                    border-radius: 18px;
                    padding: 28px;
                    text-align: center;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.35);
                    border-top: 8px solid #F88A17;
                ">
                    <div style="
                        width: 54px;
                        height: 54px;
                        margin: 0 auto 18px;
                        border: 6px solid #f5d1a6;
                        border-top: 6px solid #F88A17;
                        border-radius: 50%;
                        animation: madbotFaceSpin 0.9s linear infinite;
                    "></div>
                    <div style="font-size: 22px; font-weight: bold; color: #006060;">
                        Loading MadBot Face AI
                    </div>
                    <div id="madbot-face-ai-loader-message" style="
                        margin-top: 12px;
                        color: #575E75;
                        font-size: 15px;
                        line-height: 1.4;
                    ">
                        Please wait...
                    </div>
                </div>
            `;

            const style = document.createElement('style');
            style.textContent = `
                @keyframes madbotFaceSpin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `;
            document.head.appendChild(style);
            document.body.appendChild(popup);
        }

        const messageNode = document.getElementById('madbot-face-ai-loader-message');
        if (messageNode) messageNode.textContent = message;
    }

    hideLoadingPopup () {
        const popup = document.getElementById('madbot-face-ai-loader');
        if (popup) popup.remove();
    }

    showLoadingError (error) {
        const popup = document.getElementById('madbot-face-ai-loader');

        if (!popup) {
            console.warn('MadBot Face AI error:', error);
            return;
        }

        popup.innerHTML = `
            <div style="
                width: 420px;
                background: #ffffff;
                border-radius: 18px;
                padding: 28px;
                text-align: center;
                box-shadow: 0 12px 40px rgba(0,0,0,0.35);
                border-top: 8px solid #ff3131;
                font-family: Arial, sans-serif;
            ">
                <div style="font-size: 22px; font-weight: bold; color: #ff3131;">
                    MadBot Face AI failed to load
                </div>
                <div style="
                    margin-top: 12px;
                    color: #575E75;
                    font-size: 14px;
                    line-height: 1.4;
                    word-break: break-word;
                ">
                    ${error && error.message ? error.message : error}
                </div>
                <button id="madbot-face-ai-close-error" style="
                    margin-top: 20px;
                    background: #F88A17;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 10px 18px;
                    font-weight: bold;
                    cursor: pointer;
                ">
                    Close
                </button>
            </div>
        `;

        const closeButton = document.getElementById('madbot-face-ai-close-error');
        if (closeButton) closeButton.onclick = () => this.hideLoadingPopup();
    }

    async initialize () {
        if (this.initialized) return true;
        if (this.initializePromise) return this.initializePromise;

        this.initializePromise = this._initialize();
        return this.initializePromise;
    }

    async _initialize () {
        this.initializing = true;

        this.showLoadingPopup('Loading face AI library...');
        await this.loadFaceApiScript();

        if (!window.faceapi) {
            throw new Error('face-api.js could not be loaded.');
        }

        this.showLoadingPopup('Loading face detection models...');
        await this.loadModels();

        this.rebuildFaceMatcher();

        this.initialized = true;
        this.initializing = false;

        this.showLoadingPopup('MadBot Face AI is ready.');
        setTimeout(() => this.hideLoadingPopup(), 500);

        return true;
    }

    loadScriptFrom (src) {
        return new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing) {
                if (window.faceapi) {
                    resolve();
                } else {
                    existing.addEventListener('load', resolve);
                    existing.addEventListener('error', reject);
                }
                return;
            }

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = resolve;
            script.onerror = () => reject(new Error(`Could not load script: ${src}`));
            document.head.appendChild(script);
        });
    }

    async loadFaceApiScript () {
        if (window.faceapi) return;

        try {
            await this.loadScriptFrom(FACE_API_LOCAL_SCRIPT);
        } catch (localError) {
            this.showLoadingPopup('Local library not found. Loading from internet...');
            await this.loadScriptFrom(FACE_API_CDN_SCRIPT);
        }
    }

    async loadModels () {
        try {
            await this.loadModelsFrom(FACE_API_LOCAL_MODEL_URL);
        } catch (localError) {
            this.showLoadingPopup('Local models not found. Loading models from internet...');
            await this.loadModelsFrom(FACE_API_CDN_MODEL_URL);
        }
    }

    async loadModelsFrom (modelUrl) {
        await Promise.all([
            window.faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl),
            window.faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl),
            window.faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl),
            window.faceapi.nets.faceExpressionNet.loadFromUri(modelUrl)
        ]);
    }

    findStageHost () {
        const candidates = Array.from(document.querySelectorAll('div'))
            .filter(el => {
                const rect = el.getBoundingClientRect();
                const hasCanvas = !!el.querySelector('canvas');
                const goodSize = rect.width > 250 && rect.height > 180;
                const ratio = rect.width / Math.max(1, rect.height);
                const stageRatio = ratio > 1.1 && ratio < 1.7;
                return hasCanvas && goodSize && stageRatio;
            });

        if (candidates.length > 0) {
            return candidates[candidates.length - 1];
        }

        return (
            document.querySelector('[class*="stage-wrapper"]') ||
            document.querySelector('[class*="stage_stage"]') ||
            document.body
        );
    }

    createPreviewElements () {
        if (this.container && this.video && this.canvas) return;

        this.stageHost = this.findStageHost();
        const mountNode = this.stageHost || document.body;

        if (mountNode !== document.body) {
            const computed = window.getComputedStyle(mountNode);
            if (computed.position === 'static') {
                mountNode.style.position = 'relative';
            }
        }

        this.container = document.createElement('div');
        this.container.id = 'madbot-face-ai-overlay';

        Object.assign(this.container.style, {
            position: mountNode === document.body ? 'fixed' : 'absolute',
            left: '0',
            top: '0',
            width: mountNode === document.body ? '320px' : '100%',
            height: mountNode === document.body ? '240px' : '100%',
            right: mountNode === document.body ? '16px' : 'auto',
            bottom: mountNode === document.body ? '16px' : 'auto',
            background: 'transparent',
            overflow: 'hidden',
            zIndex: '50',
            display: this.showPreview ? 'block' : 'none',
            pointerEvents: 'none',
            border: 'none',
            borderRadius: '0'
        });

        this.canvas = document.createElement('canvas');
        this.canvas.width = 480;
        this.canvas.height = 360;

        Object.assign(this.canvas.style, {
            position: 'absolute',
            left: '0',
            top: '0',
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
        });

        this.ctx = this.canvas.getContext('2d');
        this.container.appendChild(this.canvas);
        mountNode.appendChild(this.container);

        this.video = document.createElement('video');
        this.video.autoplay = true;
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', 'true');
        this.video.setAttribute('webkit-playsinline', 'true');

        Object.assign(this.video.style, {
            position: 'fixed',
            left: '-10000px',
            top: '-10000px',
            width: '1px',
            height: '1px',
            opacity: '0',
            pointerEvents: 'none'
        });

        document.body.appendChild(this.video);
    }

    enableScratchStageVideo () {
        const videoDevice = this.runtime &&
            this.runtime.ioDevices &&
            this.runtime.ioDevices.video;

        if (videoDevice && videoDevice.enableVideo) {
            videoDevice.enableVideo();
        }

        this.syncScratchVideoSource();
        setTimeout(() => this.syncScratchVideoSource(), 100);
        setTimeout(() => this.syncScratchVideoSource(), 400);

        if (videoDevice && videoDevice.setPreviewGhost) {
            videoDevice.setPreviewGhost(this.cameraTransparency);
        }

        if (this.container) {
            this.container.style.display = this.showPreview ? 'block' : 'none';
        }
    }

    syncScratchVideoSource () {
        const videoDevice = this.runtime &&
            this.runtime.ioDevices &&
            this.runtime.ioDevices.video;

        if (!videoDevice || !this.stream) return;

        const possibleVideos = [
            videoDevice._video,
            videoDevice.video,
            videoDevice._videoElement
        ];

        const scratchVideo = possibleVideos.find(item => {
            if (!item) return false;
            if (typeof HTMLVideoElement === 'undefined') return item.tagName === 'VIDEO';
            return item instanceof HTMLVideoElement;
        });

        if (!scratchVideo) return;

        try {
            const oldStream = scratchVideo.srcObject;
            if (oldStream && oldStream !== this.stream && oldStream.getTracks) {
                oldStream.getTracks().forEach(track => {
                    try {
                        track.stop();
                    } catch (e) {
                        // Ignore old Scratch stream stop errors.
                    }
                });
            }

            if (scratchVideo.srcObject !== this.stream) {
                scratchVideo.srcObject = this.stream;
            }

            scratchVideo.autoplay = true;
            scratchVideo.muted = true;
            scratchVideo.playsInline = true;
            scratchVideo.setAttribute('playsinline', 'true');
            scratchVideo.setAttribute('webkit-playsinline', 'true');

            if (scratchVideo.play) {
                const playResult = scratchVideo.play();
                if (playResult && playResult.catch) {
                    playResult.catch(() => {});
                }
            }
        } catch (error) {
            console.warn('MadBot Face AI: could not sync Scratch video source:', error);
        }
    }

    disableScratchStageVideo () {
        const videoDevice = this.runtime &&
            this.runtime.ioDevices &&
            this.runtime.ioDevices.video;

        if (videoDevice && videoDevice.disableVideo) {
            videoDevice.disableVideo();
        }
    }

    async turnCamera (state) {
        if (state === 'on') {
            await this.startCamera();
        } else {
            this.stopCamera();
        }
    }

    async startCamera () {
        await this.initialize();

        this.createPreviewElements();

        this.runningLoop = false;
        this.disableScratchStageVideo();
        this.stopCameraStreamOnly();

        await this.sleep(300);

        this.stream = await this.openSelectedCameraStream();

        this.video.srcObject = this.stream;
        this.video.autoplay = true;
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', 'true');
        this.video.setAttribute('webkit-playsinline', 'true');

        await this.waitForVideoReady();
        await this.video.play();

        this.cameraOn = true;

        if (this.showPreview) {
            this.enableScratchStageVideo();
        }

        this.startDetectionLoop();
    }

    stopCamera () {
        this.cameraOn = false;
        this.runningLoop = false;

        this.disableScratchStageVideo();
        this.stopCameraStreamOnly();

        this.faces = [];
        this.clearCanvas();
    }

    stopCameraStreamOnly () {
        if (this.stream) {
            this.stream.getTracks().forEach(track => {
                try {
                    track.stop();
                } catch (e) {
                    // Ignore track stop errors.
                }
            });
            this.stream = null;
        }

        if (this.video) {
            try {
                this.video.pause();
                this.video.srcObject = null;
                this.video.load();
            } catch (e) {
                // Ignore video reset errors.
            }
        }
    }

    async restartCameraForSourceChange () {
        const shouldRestartLoop = this.runningLoop || this.cameraOn;

        this.runningLoop = false;
        this.disableScratchStageVideo();
        this.stopCameraStreamOnly();

        await this.sleep(400);

        if (shouldRestartLoop) {
            await this.startCamera();
        }
    }

    async setCameraSource (source) {
        const cleanSource = String(source || 'front').toLowerCase();
        this.cameraSource = cleanSource === 'back' || cleanSource === 'environment' ? 'back' : 'front';

        if (this.cameraOn) {
            await this.restartCameraForSourceChange();
        }
    }

    async openSelectedCameraStream () {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('Camera is not supported in this browser.');
        }

        const facingMode = this.cameraSource === 'back' ? 'environment' : 'user';

        const baseVideoSettings = {
            width: {ideal: 640},
            height: {ideal: 480}
        };

        const deviceId = await this.findCameraDeviceId(this.cameraSource);

        if (deviceId) {
            try {
                return await navigator.mediaDevices.getUserMedia({
                    video: {
                        ...baseVideoSettings,
                        deviceId: {exact: deviceId}
                    },
                    audio: false
                });
            } catch (error) {
                console.warn('MadBot Face AI: deviceId camera failed, trying exact facingMode.', error);
            }
        }

        try {
            return await navigator.mediaDevices.getUserMedia({
                video: {
                    ...baseVideoSettings,
                    facingMode: {exact: facingMode}
                },
                audio: false
            });
        } catch (error) {
            console.warn('MadBot Face AI: exact facingMode failed, trying ideal facingMode.', error);
        }

        return await navigator.mediaDevices.getUserMedia({
            video: {
                ...baseVideoSettings,
                facingMode: {ideal: facingMode}
            },
            audio: false
        });
    }

    async findCameraDeviceId (source) {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
            return null;
        }

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');

            if (videoDevices.length < 1) return null;

            const backKeywords = ['back', 'rear', 'environment', 'world'];
            const frontKeywords = ['front', 'user', 'face', 'selfie'];
            const keywords = source === 'back' ? backKeywords : frontKeywords;

            const matchedDevice = videoDevices.find(device => {
                const label = String(device.label || '').toLowerCase();
                return keywords.some(keyword => label.includes(keyword));
            });

            if (matchedDevice) {
                return matchedDevice.deviceId;
            }

            if (source === 'back' && videoDevices.length > 1) {
                return videoDevices[videoDevices.length - 1].deviceId;
            }

            if (source === 'front') {
                return videoDevices[0].deviceId;
            }

            return null;
        } catch (error) {
            console.warn('MadBot Face AI: enumerateDevices failed.', error);
            return null;
        }
    }

    waitForVideoReady () {
        if (!this.video) return Promise.resolve();

        if (this.video.readyState >= 2 && this.video.videoWidth > 0) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            const finish = () => {
                this.video.removeEventListener('loadedmetadata', finish);
                this.video.removeEventListener('canplay', finish);
                resolve();
            };

            this.video.addEventListener('loadedmetadata', finish);
            this.video.addEventListener('canplay', finish);

            setTimeout(finish, 1200);
        });
    }

    sleep (ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    setPreview (visible) {
        this.showPreview = visible;

        if (this.container) {
            this.container.style.display = visible ? 'block' : 'none';
        }

        if (visible && this.cameraOn) {
            this.enableScratchStageVideo();
        } else {
            this.disableScratchStageVideo();
        }
    }

    setCameraTransparency (value) {
        this.cameraTransparency = Math.max(0, Math.min(100, Cast.toNumber(value)));

        const videoDevice = this.runtime &&
            this.runtime.ioDevices &&
            this.runtime.ioDevices.video;

        if (videoDevice && videoDevice.setPreviewGhost) {
            videoDevice.setPreviewGhost(this.cameraTransparency);
        }
    }

    setDetectionMode (mode) {
        this.detectionMode = mode;
    }

    setConfidenceThreshold (value) {
        this.confidenceThreshold = Math.max(1, Math.min(100, Number(value) || 70));
    }

    setOverlayDisplay (overlayName, state) {
        const overlayMap = {
            'face box': 'faceBox',
            'name label': 'nameLabel',
            'expression label': 'expressionLabel',
            'face points': 'facePoints'
        };

        const key = overlayMap[overlayName];

        if (key) {
            this.overlay[key] = state === 'on';
        }
    }

    startDetectionLoop () {
        if (this.runningLoop) return;
        this.runningLoop = true;

        const loop = async () => {
            if (!this.cameraOn || !this.runningLoop) return;

            try {
                await this.detectOnce();
            } catch (e) {
                console.warn('MadBot Face AI detection error:', e);
            }

            const delay = this.detectionMode === 'fast' ? 160 : 280;
            setTimeout(loop, delay);
        };

        loop();
    }

    getDetectorOptions () {
        const scoreThreshold = this.confidenceThreshold / 100;
        const inputSize = this.detectionMode === 'fast' ? 224 : 416;

        return new window.faceapi.TinyFaceDetectorOptions({
            inputSize,
            scoreThreshold
        });
    }

    async detectOnce () {
        if (!this.video || this.video.readyState < 2) return;

        const results = await window.faceapi
            .detectAllFaces(this.video, this.getDetectorOptions())
            .withFaceLandmarks()
            .withFaceExpressions()
            .withFaceDescriptors();

        this.faces = results.map((result, index) => this.normalizeFace(result, index));
        this.applyRecognition();
        this.drawOverlay();
    }

    normalizeFace (result, index) {
        const box = result.detection.box;
        const landmarks = result.landmarks;
        const expressions = result.expressions || {};

        const leftEye = this.averagePoint(landmarks.getLeftEye());
        const rightEye = this.averagePoint(landmarks.getRightEye());
        const nose = this.averagePoint(landmarks.getNose());
        const mouth = this.averagePoint(landmarks.getMouth());

        const dominantExpression = this.getDominantExpression(expressions);
        const headAngle = this.getHeadAngle(leftEye, rightEye);

        return {
            index,
            raw: result,
            descriptor: result.descriptor,
            score: result.detection.score,
            box,
            expressions,
            dominantExpression,
            dominantExpressionConfidence: expressions[this.expressionMap().toFaceApi[dominantExpression]] || 0,
            points: {
                leftEye,
                rightEye,
                nose,
                mouth
            },
            headAngle,
            recognizedName: '',
            recognizedConfidence: 0
        };
    }

    averagePoint (points) {
        if (!points || !points.length) return {x: 0, y: 0};

        const sum = points.reduce((acc, p) => ({
            x: acc.x + p.x,
            y: acc.y + p.y
        }), {x: 0, y: 0});

        return {
            x: sum.x / points.length,
            y: sum.y / points.length
        };
    }

    expressionMap () {
        return {
            toFaceApi: {
                happy: 'happy',
                sad: 'sad',
                angry: 'angry',
                surprised: 'surprised',
                neutral: 'neutral',
                fear: 'fearful',
                disgust: 'disgusted'
            },
            reverse: {
                happy: 'happy',
                sad: 'sad',
                angry: 'angry',
                surprised: 'surprised',
                neutral: 'neutral',
                fearful: 'fear',
                disgusted: 'disgust'
            }
        };
    }

    getDominantExpression (expressions) {
        const reverse = this.expressionMap().reverse;
        let best = 'neutral';
        let bestValue = 0;

        Object.keys(expressions).forEach(key => {
            const userKey = reverse[key] || key;
            if (expressions[key] > bestValue) {
                best = userKey;
                bestValue = expressions[key];
            }
        });

        return best;
    }

    getExpressionConfidence (expression, faceIndex) {
        const face = this.getFace(faceIndex);
        if (!face) return 0;

        const apiExpression = this.expressionMap().toFaceApi[expression] || expression;
        return Math.round((face.expressions[apiExpression] || 0) * 100);
    }

    getHeadAngle (leftEye, rightEye) {
        const dx = rightEye.x - leftEye.x;
        const dy = rightEye.y - leftEye.y;
        if (!dx) return 0;
        return Math.round(Math.atan2(dy, dx) * 180 / Math.PI);
    }

    getFace (faceIndex) {
        const index = Math.max(0, Cast.toNumber(faceIndex) - 1);
        return this.faces[index] || null;
    }

    videoWidth () {
        return this.video && this.video.videoWidth ? this.video.videoWidth : 640;
    }

    videoHeight () {
        return this.video && this.video.videoHeight ? this.video.videoHeight : 480;
    }

    isPreviewMirrored () {
        return this.cameraSource === 'front';
    }

    getPreviewX (videoX) {
        if (this.isPreviewMirrored()) {
            return this.videoWidth() - videoX;
        }
        return videoX;
    }

    getPreviewBoxX (box) {
        if (this.isPreviewMirrored()) {
            return this.videoWidth() - (box.x + box.width);
        }
        return box.x;
    }

    toStageX (videoX) {
        const previewX = this.getPreviewX(videoX);
        return Math.round((previewX / this.videoWidth()) * 480 - 240);
    }

    toStageY (videoY) {
        return Math.round(180 - ((videoY / this.videoHeight()) * 360));
    }

    getFaceProperty (faceIndex, property) {
        const face = this.getFace(faceIndex);
        if (!face) return 0;

        const box = face.box;
        const previewBoxX = this.getPreviewBoxX(box);
        const previewCenterX = previewBoxX + (box.width / 2);
        const previewCenterY = box.y + (box.height / 2);

        const previewLeftEyeX = this.getPreviewX(face.points.leftEye.x);
        const previewRightEyeX = this.getPreviewX(face.points.rightEye.x);
        const previewNoseX = this.getPreviewX(face.points.nose.x);
        const previewMouthX = this.getPreviewX(face.points.mouth.x);

        switch (property) {
        case 'x position':
            return Math.round((previewBoxX / this.videoWidth()) * 480 - 240);
        case 'y position':
            return this.toStageY(box.y);
        case 'width':
            return Math.round((box.width / this.videoWidth()) * 480);
        case 'height':
            return Math.round((box.height / this.videoHeight()) * 360);
        case 'center x':
            return Math.round((previewCenterX / this.videoWidth()) * 480 - 240);
        case 'center y':
            return this.toStageY(previewCenterY);
        case 'distance level':
            return Math.round(Math.min(100, (box.width / this.videoWidth()) * 250));
        case 'left eye x':
            return Math.round((previewLeftEyeX / this.videoWidth()) * 480 - 240);
        case 'left eye y':
            return this.toStageY(face.points.leftEye.y);
        case 'right eye x':
            return Math.round((previewRightEyeX / this.videoWidth()) * 480 - 240);
        case 'right eye y':
            return this.toStageY(face.points.rightEye.y);
        case 'nose x':
            return Math.round((previewNoseX / this.videoWidth()) * 480 - 240);
        case 'nose y':
            return this.toStageY(face.points.nose.y);
        case 'mouth x':
            return Math.round((previewMouthX / this.videoWidth()) * 480 - 240);
        case 'mouth y':
            return this.toStageY(face.points.mouth.y);
        case 'head angle':
            return face.headAngle;
        default:
            return 0;
        }
    }

    faceLooking (faceIndex, direction) {
        const face = this.getFace(faceIndex);
        if (!face) return false;

        const box = face.box;
        const centerX = box.x + box.width / 2;
        const centerY = box.y + box.height / 2;
        const noseX = face.points.nose.x;
        const noseY = face.points.nose.y;

        let dx = noseX - centerX;
        const dy = noseY - centerY;

        if (this.isPreviewMirrored()) {
            dx = -dx;
        }

        const xLimit = box.width * 0.08;
        const yLimit = box.height * 0.08;

        if (direction === 'left') return dx < -xLimit;
        if (direction === 'right') return dx > xLimit;
        if (direction === 'up') return dy < -yLimit;
        if (direction === 'down') return dy > yLimit;
        if (direction === 'center') return Math.abs(dx) <= xLimit && Math.abs(dy) <= yLimit;

        return false;
    }

    applyRecognition () {
        if (!this.recognitionEnabled) {
            this.faces.forEach(face => {
                face.recognizedName = '';
                face.recognizedConfidence = 0;
            });
            this.lastRecognizedName = '';
            this.lastRecognizedConfidence = 0;
            return;
        }

        if (!this.faceMatcher) {
            this.faces.forEach(face => {
                face.recognizedName = 'unknown';
                face.recognizedConfidence = 0;
            });
            this.lastRecognizedName = 'unknown';
            this.lastRecognizedConfidence = 0;
            return;
        }

        this.faces.forEach(face => {
            if (!face.descriptor) return;

            const match = this.faceMatcher.findBestMatch(face.descriptor);
            const name = match.label || 'unknown';
            const distance = match.distance || 1;

            face.recognizedName = name === 'unknown' ? 'unknown' : name;
            face.recognizedConfidence = Math.round(Math.max(0, Math.min(100, (1 - distance) * 100)));

            this.lastRecognizedName = face.recognizedName;
            this.lastRecognizedConfidence = face.recognizedConfidence;
        });
    }

    async trainFace (name) {
        await this.initialize();

        const cleanName = String(name || '').trim();
        if (!cleanName) return false;

        const face = this.getFace(1);
        if (!face || !face.descriptor) return false;

        if (!this.trainedFaces[cleanName]) {
            this.trainedFaces[cleanName] = [];
        }

        this.trainedFaces[cleanName].push(Array.from(face.descriptor));
        this.rebuildFaceMatcher();
        this.saveDatabase();

        return true;
    }

    recognizeFace () {
        this.recognitionEnabled = true;
        this.applyRecognition();
        return this.lastRecognizedName || 'unknown';
    }

    isRecognizedAs (name) {
        return this.recognitionEnabled &&
            this.lastRecognizedName.toLowerCase() === String(name || '').toLowerCase();
    }

    rebuildFaceMatcher () {
        if (!window.faceapi) return;

        const labeledDescriptors = Object.keys(this.trainedFaces).map(name => {
            const descriptors = this.trainedFaces[name].map(arr => new Float32Array(arr));
            return new window.faceapi.LabeledFaceDescriptors(name, descriptors);
        });

        this.faceMatcher = labeledDescriptors.length ?
            new window.faceapi.FaceMatcher(labeledDescriptors, 0.6) :
            null;
    }

    deleteFaceData (name) {
        delete this.trainedFaces[String(name || '').trim()];
        this.rebuildFaceMatcher();
        this.saveDatabase();
    }

    deleteAllFaces () {
        this.trainedFaces = {};
        this.faceMatcher = null;
        this.lastRecognizedName = '';
        this.lastRecognizedConfidence = 0;
        this.recognitionEnabled = false;
        this.saveDatabase();
    }

    numberOfTrainedFaces () {
        return Object.keys(this.trainedFaces).length;
    }

    saveDatabase () {
        try {
            localStorage.setItem('madbotFaceAI.trainedFaces', JSON.stringify(this.trainedFaces));
        } catch (e) {
            console.warn('Could not save MadBot face database:', e);
        }
    }

    loadDatabase () {
        try {
            const saved = localStorage.getItem('madbotFaceAI.trainedFaces');
            if (saved) this.trainedFaces = JSON.parse(saved) || {};
        } catch (e) {
            this.trainedFaces = {};
        }

        if (window.faceapi) {
            this.rebuildFaceMatcher();
        }
    }

    clearCanvas () {
        if (this.ctx && this.canvas) {
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }
    }

    drawOverlay () {
        if (!this.ctx || !this.canvas || !this.video) return;

        const ctx = this.ctx;
        const width = this.canvas.width;
        const height = this.canvas.height;

        ctx.clearRect(0, 0, width, height);

        // Important fix:
        // Do not draw the camera image on this custom canvas.
        // This canvas is above the Scratch stage, so drawing video here hides sprites.
        // Scratch's own video preview is used behind sprites.
        // This canvas draws only face boxes, points, and labels.

        const videoWidth = this.videoWidth();
        const videoHeight = this.videoHeight();
        const scaleX = width / videoWidth;
        const scaleY = height / videoHeight;

        this.faces.forEach(face => {
            const box = face.box;

            const drawX = this.getPreviewBoxX(box) * scaleX;
            const drawY = box.y * scaleY;
            const drawW = box.width * scaleX;
            const drawH = box.height * scaleY;

            if (this.overlay.faceBox) {
                ctx.strokeStyle = '#F88A17';
                ctx.lineWidth = 3;
                ctx.strokeRect(drawX, drawY, drawW, drawH);
            }

            if (this.overlay.facePoints) {
                ctx.fillStyle = '#00FFFF';

                Object.values(face.points).forEach(point => {
                    const px = this.getPreviewX(point.x) * scaleX;
                    const py = point.y * scaleY;

                    ctx.beginPath();
                    ctx.arc(px, py, 4, 0, Math.PI * 2);
                    ctx.fill();
                });
            }

            const labels = [];

            if (this.overlay.nameLabel && this.recognitionEnabled && face.recognizedName) {
                labels.push(face.recognizedName);
            }

            if (this.overlay.expressionLabel) {
                labels.push(face.dominantExpression);
            }

            if (labels.length > 0) {
                const labelWidth = Math.max(100, drawW);

                ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
                ctx.fillRect(drawX, Math.max(0, drawY - 26), labelWidth, 24);

                ctx.fillStyle = '#FFFFFF';
                ctx.font = '15px Arial';
                ctx.fillText(labels.join(' | '), drawX + 6, Math.max(18, drawY - 8));
            }
        });
    }
}

const getEngine = () => {
    if (!window.__madbotFaceAIEngine) {
        window.__madbotFaceAIEngine = new MadBotFaceAIEngine();
    }
    return window.__madbotFaceAIEngine;
};

class Scratch3MadBotFaceAI {
    constructor (runtime) {
        this.runtime = runtime;
        this.engine = getEngine();
        this.engine.setRuntime(runtime);
    }

    getInfo () {
        return {
            id: 'madbotFaceAI',
            name: 'MadBot Face AI',
            color1: '#2033B0',
            color2: '#18288F',
            color3: '#101E6E',
            blocks: [
                sectionLabel('Settings'),
                {
                    opcode: 'initializeFaceAI',
                    blockType: BlockType.COMMAND,
                    text: 'initialize face AI'
                },
                {
                    opcode: 'turnCamera',
                    blockType: BlockType.COMMAND,
                    text: 'turn camera [STATE] transparency [TRANSPARENCY] %',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOff',
                            defaultValue: 'on'
                        },
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        }
                    }
                },
                {
                    opcode: 'setCameraSource',
                    blockType: BlockType.COMMAND,
                    text: 'set camera source [SOURCE]',
                    arguments: {
                        SOURCE: {
                            type: ArgumentType.STRING,
                            menu: 'cameraSource',
                            defaultValue: 'front'
                        }
                    }
                },
                {
                    opcode: 'setCameraPreview',
                    blockType: BlockType.COMMAND,
                    text: 'camera preview [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'showHide',
                            defaultValue: 'show'
                        }
                    }
                },
                {
                    opcode: 'setDetectionMode',
                    blockType: BlockType.COMMAND,
                    text: 'set detection mode [MODE]',
                    arguments: {
                        MODE: {
                            type: ArgumentType.STRING,
                            menu: 'detectionMode',
                            defaultValue: 'fast'
                        }
                    }
                },
                {
                    opcode: 'setConfidenceThreshold',
                    blockType: BlockType.COMMAND,
                    text: 'set confidence threshold to [VALUE] %',
                    arguments: {
                        VALUE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 70
                        }
                    }
                },
                {
                    opcode: 'setOverlayDisplay',
                    blockType: BlockType.COMMAND,
                    text: 'set [OVERLAY] [STATE]',
                    arguments: {
                        OVERLAY: {
                            type: ArgumentType.STRING,
                            menu: 'overlayOptions',
                            defaultValue: 'face box'
                        },
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOff',
                            defaultValue: 'off'
                        }
                    }
                },

                '---',
                sectionLabel('Face Detection'),
                {
                    opcode: 'faceProperty',
                    blockType: BlockType.REPORTER,
                    text: 'face [FACE] [PROPERTY]',
                    arguments: {
                        FACE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        },
                        PROPERTY: {
                            type: ArgumentType.STRING,
                            menu: 'faceProperties',
                            defaultValue: 'x position'
                        }
                    }
                },
                {
                    opcode: 'faceDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'face detected?'
                },
                {
                    opcode: 'numberOfFacesDetected',
                    blockType: BlockType.REPORTER,
                    text: 'number of faces detected'
                },
                {
                    opcode: 'faceLooking',
                    blockType: BlockType.BOOLEAN,
                    text: 'face [FACE] looking [DIRECTION]?',
                    arguments: {
                        FACE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        },
                        DIRECTION: {
                            type: ArgumentType.STRING,
                            menu: 'directions',
                            defaultValue: 'center'
                        }
                    }
                },

                '---',
                sectionLabel('Face Expression'),
                {
                    opcode: 'expressionOfFace',
                    blockType: BlockType.REPORTER,
                    text: 'expression of face [FACE]',
                    arguments: {
                        FACE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },
                {
                    opcode: 'faceIsExpression',
                    blockType: BlockType.BOOLEAN,
                    text: 'face [FACE] is [EXPRESSION]?',
                    arguments: {
                        FACE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        },
                        EXPRESSION: {
                            type: ArgumentType.STRING,
                            menu: 'expressions',
                            defaultValue: 'happy'
                        }
                    }
                },
                {
                    opcode: 'confidenceOfExpression',
                    blockType: BlockType.REPORTER,
                    text: 'confidence of expression [EXPRESSION] for face [FACE]',
                    arguments: {
                        EXPRESSION: {
                            type: ArgumentType.STRING,
                            menu: 'expressions',
                            defaultValue: 'happy'
                        },
                        FACE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },
                {
                    opcode: 'dominantExpressionConfidence',
                    blockType: BlockType.REPORTER,
                    text: 'dominant expression confidence of face [FACE]',
                    arguments: {
                        FACE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },

                '---',
                sectionLabel('Face Recognition: Training'),
                {
                    opcode: 'trainFaceAs',
                    blockType: BlockType.COMMAND,
                    text: 'train face as [NAME]',
                    arguments: {
                        NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Nikunj'
                        }
                    }
                },
                {
                    opcode: 'recognizeFace',
                    blockType: BlockType.COMMAND,
                    text: 'recognize face'
                },

                '---',
                sectionLabel('Face Recognition: Results'),
                {
                    opcode: 'recognizedPersonName',
                    blockType: BlockType.REPORTER,
                    text: 'recognized person name'
                },
                {
                    opcode: 'recognizedPersonConfidence',
                    blockType: BlockType.REPORTER,
                    text: 'recognized person confidence'
                },
                {
                    opcode: 'faceIsRecognizedAs',
                    blockType: BlockType.BOOLEAN,
                    text: 'face is recognized as [NAME]?',
                    arguments: {
                        NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Nikunj'
                        }
                    }
                },

                '---',
                sectionLabel('Recognition Data'),
                {
                    opcode: 'deleteFaceDataOf',
                    blockType: BlockType.COMMAND,
                    text: 'delete face data of [NAME]',
                    arguments: {
                        NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Nikunj'
                        }
                    }
                },
                {
                    opcode: 'deleteAllTrainedFaces',
                    blockType: BlockType.COMMAND,
                    text: 'delete all trained faces'
                },
                {
                    opcode: 'numberOfTrainedFaces',
                    blockType: BlockType.REPORTER,
                    text: 'number of trained faces'
                },
                {
                    opcode: 'saveFaceDatabase',
                    blockType: BlockType.COMMAND,
                    text: 'save face database'
                },
                {
                    opcode: 'loadFaceDatabase',
                    blockType: BlockType.COMMAND,
                    text: 'load face database'
                },

                '---',
                sectionLabel('Events'),
                {
                    opcode: 'whenFaceDetected',
                    blockType: BlockType.HAT,
                    text: 'when face detected',
                    isEdgeActivated: true
                },
                {
                    opcode: 'whenNoFaceDetected',
                    blockType: BlockType.HAT,
                    text: 'when no face detected',
                    isEdgeActivated: true
                },
                {
                    opcode: 'whenExpressionBecomes',
                    blockType: BlockType.HAT,
                    text: 'when expression becomes [EXPRESSION]',
                    isEdgeActivated: true,
                    arguments: {
                        EXPRESSION: {
                            type: ArgumentType.STRING,
                            menu: 'expressions',
                            defaultValue: 'happy'
                        }
                    }
                },
                {
                    opcode: 'whenPersonRecognized',
                    blockType: BlockType.HAT,
                    text: 'when person [NAME] recognized',
                    isEdgeActivated: true,
                    arguments: {
                        NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Nikunj'
                        }
                    }
                },
                {
                    opcode: 'whenUnknownFaceDetected',
                    blockType: BlockType.HAT,
                    text: 'when unknown face detected',
                    isEdgeActivated: true
                }
            ],
            menus: {
                showHide: {
                    acceptReporters: true,
                    items: ['show', 'hide']
                },
                overlayOptions: {
                    acceptReporters: true,
                    items: [
                        'face box',
                        'name label',
                        'expression label',
                        'face points'
                    ]
                },
                onOff: {
                    acceptReporters: true,
                    items: ['on', 'off']
                },
                cameraSource: {
                    acceptReporters: true,
                    items: ['front', 'back']
                },
                detectionMode: {
                    acceptReporters: true,
                    items: ['fast', 'accurate']
                },
                faceProperties: {
                    acceptReporters: true,
                    items: [
                        'x position',
                        'y position',
                        'width',
                        'height',
                        'center x',
                        'center y',
                        'distance level',
                        'left eye x',
                        'left eye y',
                        'right eye x',
                        'right eye y',
                        'nose x',
                        'nose y',
                        'mouth x',
                        'mouth y',
                        'head angle'
                    ]
                },
                directions: {
                    acceptReporters: true,
                    items: ['left', 'right', 'up', 'down', 'center']
                },
                expressions: {
                    acceptReporters: true,
                    items: [
                        'happy',
                        'sad',
                        'angry',
                        'surprised',
                        'neutral',
                        'fear',
                        'disgust'
                    ]
                }
            }
        };
    }

    initializeFaceAI () {
        return this.engine.initialize();
    }

    turnCamera (args) {
        this.engine.setCameraTransparency(args.TRANSPARENCY);
        return this.engine.turnCamera(args.STATE);
    }

    setCameraSource (args) {
        return this.engine.setCameraSource(args.SOURCE);
    }

    setCameraPreview (args) {
        this.engine.setPreview(args.STATE === 'show');
    }

    setDetectionMode (args) {
        this.engine.setDetectionMode(args.MODE);
    }

    setConfidenceThreshold (args) {
        this.engine.setConfidenceThreshold(args.VALUE);
    }

    setOverlayDisplay (args) {
        this.engine.setOverlayDisplay(args.OVERLAY, args.STATE);
    }

    faceProperty (args) {
        return this.engine.getFaceProperty(args.FACE, args.PROPERTY);
    }

    faceDetected () {
        return this.engine.faces.length > 0;
    }

    numberOfFacesDetected () {
        return this.engine.faces.length;
    }

    faceLooking (args) {
        return this.engine.faceLooking(args.FACE, args.DIRECTION);
    }

    expressionOfFace (args) {
        const face = this.engine.getFace(args.FACE);
        return face ? face.dominantExpression : 'none';
    }

    faceIsExpression (args) {
        const face = this.engine.getFace(args.FACE);
        if (!face) return false;
        return face.dominantExpression === args.EXPRESSION;
    }

    confidenceOfExpression (args) {
        return this.engine.getExpressionConfidence(args.EXPRESSION, args.FACE);
    }

    dominantExpressionConfidence (args) {
        const face = this.engine.getFace(args.FACE);
        return face ? Math.round(face.dominantExpressionConfidence * 100) : 0;
    }

    whenFaceDetected () {
        return this.engine.faces.length > 0;
    }

    whenNoFaceDetected () {
        return this.engine.cameraOn && this.engine.faces.length === 0;
    }

    whenExpressionBecomes (args) {
        const face = this.engine.getFace(1);
        return face ? face.dominantExpression === args.EXPRESSION : false;
    }

    trainFaceAs (args) {
        return this.engine.trainFace(args.NAME);
    }

    recognizeFace () {
        this.engine.recognizeFace();
    }

    recognizedPersonName () {
        return this.engine.recognitionEnabled ?
            (this.engine.lastRecognizedName || 'unknown') :
            '';
    }

    recognizedPersonConfidence () {
        return this.engine.recognitionEnabled ?
            (this.engine.lastRecognizedConfidence || 0) :
            0;
    }

    faceIsRecognizedAs (args) {
        return this.engine.isRecognizedAs(args.NAME);
    }

    deleteFaceDataOf (args) {
        this.engine.deleteFaceData(args.NAME);
    }

    deleteAllTrainedFaces () {
        this.engine.deleteAllFaces();
    }

    numberOfTrainedFaces () {
        return this.engine.numberOfTrainedFaces();
    }

    saveFaceDatabase () {
        this.engine.saveDatabase();
    }

    loadFaceDatabase () {
        this.engine.loadDatabase();
    }

    whenPersonRecognized (args) {
        return this.engine.recognitionEnabled && this.engine.isRecognizedAs(args.NAME);
    }

    whenUnknownFaceDetected () {
        return this.engine.recognitionEnabled &&
            this.engine.faces.length > 0 &&
            this.engine.lastRecognizedName === 'unknown';
    }
}

module.exports = Scratch3MadBotFaceAI;